import React, { useMemo, useState } from 'react';
import { updatePassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { auth, db } from '../firebase';

const APP_WEB_URL = 'https://lntechgestaofinanceira.netlify.app';

const isValidEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim().toLowerCase());

function PrimeiroAcesso({ user, onConcluir }) {
  const [email, setEmail] = useState(user?.email || '');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [loading, setLoading] = useState(false);

  const emailObrigatorio = useMemo(() => {
    return !user?.email || !String(user.email).includes('@');
  }, [user?.email]);

  const concluirPrimeiroAcesso = async () => {
    setErro('');
    setSucesso('');

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setErro('Sessão expirada. Faça login novamente pelo WhatsApp.');
      return;
    }

    const emailNormalizado = String(email || '').trim().toLowerCase();
    if ((emailObrigatorio || emailNormalizado) && !isValidEmail(emailNormalizado)) {
      setErro('Informe um e-mail válido para continuar.');
      return;
    }

    if (senha.length < 6) {
      setErro('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (senha !== confirmarSenha) {
      setErro('A confirmação de senha não confere.');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(currentUser, senha);

      await setDoc(doc(db, 'users', currentUser.uid), {
        email: emailNormalizado || String(user?.email || '').toLowerCase(),
        precisaDefinirSenha: false,
        atualizadoEm: new Date().toISOString()
      }, { merge: true });

      setSucesso('Senha criada com sucesso. Acesso liberado.');
      onConcluir?.({
        email: emailNormalizado || String(user?.email || '').toLowerCase(),
        precisaDefinirSenha: false
      });
    } catch (error) {
      if (error?.code === 'auth/requires-recent-login') {
        setErro('Por segurança, faça login novamente via WhatsApp e tente de novo.');
      } else if (error?.code === 'auth/email-already-in-use') {
        setErro('Este e-mail já está em uso por outra conta.');
      } else {
        setErro(`Não foi possível concluir o primeiro acesso: ${error.message || 'erro desconhecido'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, #f6fbff 0%, #e8f2ff 55%, #dce9ff 100%)',
        px: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            border: '1px solid rgba(37,99,235,0.2)',
            boxShadow: '0 24px 50px rgba(29,78,216,0.15)'
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="#1d4ed8">
            Primeiro Acesso
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Seu cadastro foi criado via WhatsApp. Defina sua senha para concluir o acesso ao sistema.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Portal: {APP_WEB_URL}
          </Typography>

          <Stack spacing={2} sx={{ mt: 3 }}>
            <TextField
              fullWidth
              required={emailObrigatorio}
              type="email"
              label="E-mail"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Nova senha"
              value={senha}
              onChange={(event) => setSenha(event.target.value)}
              helperText="Mínimo de 6 caracteres."
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Confirmar senha"
              value={confirmarSenha}
              onChange={(event) => setConfirmarSenha(event.target.value)}
            />
          </Stack>

          {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
          {sucesso && <Alert severity="success" sx={{ mt: 2 }}>{sucesso}</Alert>}

          <Button
            fullWidth
            variant="contained"
            onClick={concluirPrimeiroAcesso}
            disabled={loading}
            sx={{ mt: 3, py: 1.2, borderRadius: 2.5, fontWeight: 'bold', bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Concluir Primeiro Acesso'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}

export default PrimeiroAcesso;
